import React, { useEffect } from "react";
import { Button, Form, Input, Select, message } from "antd";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { API_METHODS } from "../../constants";
import { isEmpty } from "../../generalFunctions";
import useApi from "../../hooks/useApi";
import ServerDownResult from "../../components/common/ServerDownResult";
import HeaderComponent from "./Home/HeaderComponent";
import Footer from "./Home/Footer";

const ContactUsPage = () => {
  const [response, request] = useApi("/user/contact", null, API_METHODS.POST);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      request(values);
    });
  };

  useEffect(() => {
    if (!isEmpty(response) && !isEmpty(response.data) && !response.isLoading) {
      messageApi.open({
        type: "success",
        content: "Thank you for contacting us!",
        duration: 5,
      });

      form.resetFields();
    } else if (!isEmpty(response) && !isEmpty(response.error)) {
      messageApi.open({
        type: "error",
        content: `❌ Error in sending request`,
        duration: 4,
      });
    }
  }, [response, form, messageApi]);

  const contactInfo = [
    {
      icon: <Phone className="h-5 w-5 lg:h-6 lg:w-6" />,
      title: "Phone Support",
      value: "+91 9699588995",
      description: "Available round the clock for puja bookings and guidance",
    },
    {
      icon: <Mail className="h-5 w-5 lg:h-6 lg:w-6" />,
      title: "Email Support",
      value: "ambabaimahalaxmipuja@gmail.com",
      description: "We respond to all emails within 2 hours",
    },
    {
      icon: <MapPin className="h-5 w-5 lg:h-6 lg:w-6" />,
      title: "Temple Location",
      value: "Shree Mahalaxmi Ambabai Temple",
      description: "Kolhapur, Maharashtra 416012, India",
    },
    {
      icon: <Clock className="h-5 w-5 lg:h-6 lg:w-6" />,
      title: "Temple Timings",
      value: "Daily: 5:00 AM - 10:00 PM",
      description: "All days including festivals and special occasions",
    },
  ];

  return (
    <>
      {contextHolder}
      <HeaderComponent />
      {!isEmpty(response) ? (
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
          {/* Header */}
          <section className="py-12 lg:py-16 px-4 bg-gradient-to-r from-orange-500 to-red-500 text-white">
            <div className="container mx-auto max-w-6xl text-center">
              <div className="w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl lg:text-4xl mx-auto mb-4 lg:mb-6 animate-pulse">
                🪔
              </div>
              <h1 className="text-3xl lg:text-5xl font-bold mb-4 lg:mb-6">
                Contact Us
              </h1>
              <p className="text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed px-4">
                We're here to help you with all your spiritual needs. Reach out
                to us for puja bookings, guidance, or any questions about Shree
                Mahalaxmi Ambabai worship.
              </p>
            </div>
          </section>

          {/* Contact Info Cards */}
          <section className="py-12 lg:py-16 px-4">
            <div className="container mx-auto max-w-6xl">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-12 lg:mb-16">
                {contactInfo.map((info, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-lg p-4 lg:p-6 border border-orange-100 hover:shadow-xl transition-all transform hover:-translate-y-2"
                  >
                    <div className="text-orange-500 mb-3 lg:mb-4">
                      {info.icon}
                    </div>
                    <h3 className="text-base lg:text-lg font-semibold text-gray-800 mb-2">
                      {info.title}
                    </h3>
                    <p className="text-orange-600 font-medium mb-2 break-all sm:break-words text-xs">
                      {info.value}
                    </p>
                    <p className="text-gray-600 text-xs lg:text-sm">
                      {info.description}
                    </p>
                  </div>
                ))}
              </div>

              {/* Main Content */}
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                {/* Contact Form */}
                <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 border border-orange-100">
                  <h2 className="text-2xl lg:text-3xl font-bold text-gray-800 mb-4 lg:mb-6 flex items-center">
                    <Send className="h-6 w-6 lg:h-8 lg:w-8 mr-2 lg:mr-3 text-orange-500" />
                    Send Us a Message
                  </h2>

                  <Form layout="vertical" onFinish={handleSubmit} form={form}>
                    <div className="grid lg:grid-cols-2 gap-3 lg:gap-4">
                      <Form.Item
                        label={
                          <span className="flex items-center text-gray-700 font-medium text-sm lg:text-base">
                            Full Name *
                          </span>
                        }
                        name="name"
                        rules={[
                          {
                            required: true,
                            message: "Please input your name",
                          },
                          {
                            min: 2,
                            message: "Name must be at least 2 characters",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter your full name"
                          className="h-10 lg:h-12"
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <span className="flex items-center text-gray-700 font-medium text-sm lg:text-base">
                            Email Address *
                          </span>
                        }
                        name="email"
                        rules={[
                          {
                            required: true,
                            message: "Please input your email",
                          },
                          {
                            type: "email",
                            message: "Please enter a valid email",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="your.email@example.com"
                          className="h-10 lg:h-12"
                        />
                      </Form.Item>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-3 lg:gap-4">
                      <Form.Item
                        label={
                          <span className="flex items-center text-gray-700 font-medium text-sm lg:text-base">
                            Phone Number
                          </span>
                        }
                        name="phone"
                        rules={[
                          {
                            pattern: /^[6-9]\d{9}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="+91 98765 43210"
                          className="h-10 lg:h-12"
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <span className="flex items-center text-gray-700 font-medium text-sm lg:text-base">
                            Inquiry Type
                          </span>
                        }
                        name="subject"
                      >
                        <Select
                          size="large"
                          placeholder="Select inquiry type"
                          className="h-10 lg:h-12"
                        >
                          <Select.Option value="puja">
                            Puja Booking
                          </Select.Option>
                          <Select.Option value="guidance">
                            Spiritual Guidance
                          </Select.Option>
                          <Select.Option value="festival">
                            Festival Celebrations
                          </Select.Option>
                          <Select.Option value="technical">
                            Technical Support
                          </Select.Option>
                          <Select.Option value="feedback">
                            Feedback & Suggestions
                          </Select.Option>
                          <Select.Option value="other">
                            Other Questions
                          </Select.Option>
                        </Select>
                      </Form.Item>
                    </div>

                    <Form.Item
                      label={
                        <span className="flex items-center text-gray-700 font-medium text-sm lg:text-base">
                          Message *
                        </span>
                      }
                      name="message"
                      rules={[
                        {
                          required: true,
                          message: "Please input your message",
                        },
                        {
                          min: 10,
                          message:
                            "Message must be at least 10 characters long",
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={4}
                        placeholder="Please describe your inquiry or requirements for Mahalaxmi Ambabai puja services..."
                        className="resize-none"
                      />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        loading={response?.isLoading}
                        className="w-full h-10 lg:h-12 bg-gradient-to-r from-orange-500 to-red-500 border-none hover:from-orange-600 hover:to-red-600 font-semibold text-sm lg:text-lg"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {response?.isLoading ? (
                          "Sending Message..."
                        ) : (
                          <>Send Message</>
                        )}
                      </Button>
                    </Form.Item>

                    <p className="text-center text-xs lg:text-sm text-gray-600">
                      ⚡ We respond within 2 hours during temple hours
                    </p>
                  </Form>
                </div>

                {/* Additional Information */}
                <div className="space-y-6 lg:space-y-8">
                  {/* Quick Contact */}
                  <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 lg:p-6">
                    <h3 className="text-lg lg:text-xl font-bold text-gray-800 mb-3 lg:mb-4 flex items-center">
                      ⚡ Need Immediate Help?
                    </h3>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200">
                        <div className="flex items-center flex-1 min-w-0">
                          <Phone className="h-4 w-4 lg:h-5 lg:w-5 text-orange-500 mr-2 flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-800 text-sm">
                              Call Us Now
                            </div>
                            <div className="text-orange-600 font-medium text-xs lg:text-sm truncate">
                              +91 9699588995
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            window.open("tel:+919699588995", "_self")
                          }
                          className="bg-orange-500 text-white px-3 py-1.5 rounded-md font-medium hover:bg-orange-600 transition-all text-xs lg:text-sm flex-shrink-0 ml-2"
                        >
                          Call
                        </button>
                      </div>

                      <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-orange-200">
                        <div className="flex items-center flex-1 min-w-0">
                          <div className="text-green-500 mr-2 text-lg flex-shrink-0">
                            💬
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-gray-800 text-sm">
                              WhatsApp
                            </div>
                            <div className="text-green-600 font-medium text-xs lg:text-sm">
                              Instant Response
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() =>
                            window.open(
                              "https://wa.me/919699588995?text=Hello%20MahalaxmiPuja%20Team!%20I%20need%20help%20with%20puja%20booking.",
                              "_blank"
                            )
                          }
                          className="bg-green-500 text-white px-3 py-1.5 rounded-md font-medium hover:bg-green-600 transition-all text-xs lg:text-sm flex-shrink-0 ml-2"
                        >
                          Message
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* FAQ Section */}
                  <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8 border border-orange-100">
                    <h3 className="text-xl lg:text-2xl font-bold text-gray-800 mb-4 lg:mb-6">
                      ❓ Frequently Asked Questions
                    </h3>

                    <div className="space-y-3 lg:space-y-4">
                      {[
                        {
                          question: "How do I book a puja service?",
                          answer:
                            "You can book puja services directly through our website by selecting your preferred service, date, and time.",
                        },
                        {
                          question: "Will I receive a video of my puja?",
                          answer:
                            "Yes, we provide video recordings of all personalized pujas within 24-48 hours of completion.",
                        },
                        {
                          question:
                            "Do you deliver prasad outside Maharashtra?",
                          answer:
                            "Yes, we deliver blessed prasad across India. Delivery typically takes 3-7 working days.",
                        },
                        {
                          question: "Can I reschedule my puja booking?",
                          answer:
                            "Yes, you can reschedule your puja anytime before the ceremony begins without any additional charges.",
                        },
                      ].map((faq, index) => (
                        <div
                          key={index}
                          className="border-b border-gray-200 pb-3 lg:pb-4"
                        >
                          <h4 className="font-semibold text-gray-800 mb-1 lg:mb-2 text-sm lg:text-base">
                            {faq.question}
                          </h4>
                          <p className="text-gray-600 text-xs lg:text-sm">
                            {faq.answer}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Temple Information */}
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl p-6 lg:p-8">
                    <h3 className="text-xl lg:text-2xl font-bold mb-3 lg:mb-4 flex items-center">
                      🛕 About Our Temple
                    </h3>
                    <p className="leading-relaxed mb-3 lg:mb-4 text-sm lg:text-base">
                      Shree Mahalaxmi Ambabai Temple in Kolhapur is one of the
                      most revered Shakti Peethas in India. We have been serving
                      devotees for generations with authentic Vedic rituals and
                      spiritual guidance.
                    </p>
                    <div className="grid grid-cols-2 gap-3 lg:gap-4 text-xs lg:text-sm">
                      <div>
                        <div className="font-semibold">Established:</div>
                        <div>Ancient times</div>
                      </div>
                      <div>
                        <div className="font-semibold">Devotees Served:</div>
                        <div>25L+ annually</div>
                      </div>
                      <div>
                        <div className="font-semibold">Rituals:</div>
                        <div>Traditional Vedic</div>
                      </div>
                      <div>
                        <div className="font-semibold">Languages:</div>
                        <div> Marathi,Hindi, English</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      ) : (
        <ServerDownResult />
      )}
      <Footer />
    </>
  );
};

export default ContactUsPage;
